import { useState, useCallback, useEffect } from "react";
import {
  DateInput,
  Button,
  InputField,
  DropDownInput,
  OptionGroup,
  DataTable,
} from "@/components";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
// import WeightDisputeForm from "./WeightDisputeForm";
import { RightWrapper } from "../../../components";

import ColumnHelper from "@/utils/ColumnHelper";
import { useMemo } from "react";
import { useFilteredData } from "@/";
import { RiFileExcel2Line } from "react-icons/ri";
import {
  fetchWeightDisputes,
  selectAllWeightDisputes,
} from "../../../app/Slices";
import { useSelector, useDispatch } from "react-redux";
import { subDays } from "date-fns";
import UpdateWeightDispute from "./UpdateWeightDispute";
import { BsFiletypeXls } from "react-icons/bs";
import { HiOutlineUpload } from "react-icons/hi";

const WeightDispute = () => {
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;
  const [filters, setFilters] = useState({
    customerName: "",
    customerId: "",
    courierName: "",
    ShippingId: "",
    awbs: "",
    action: "",
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);
  const dispatch = useDispatch();

  const fetchData = async () => {
    const response = await dispatch(
      fetchWeightDisputes({
        startDate: filters.date.from,
        endDate: filters.date.to,
      })
    );
  };
  useEffect(() => {
    fetchData();
  }, [appliedFilters.date, dispatch]);

  const weightDisputes = useSelector(selectAllWeightDisputes) || [];

  const courier = useSelector((state) => state.app.appData.courier) || [];

  // Courier Options for DropDownInput
  const courierOptions = useMemo(
    () => courier.map((c) => ({ value: c._id, label: c.name })),
    []
  );
  // Ensure rawData is only generated once
  //const rawData = useMemo(() => getWeightDisputeData(100), []);

  // Filter configuration
  const filterConfig = useMemo(
    () => ({
      customerName: { type: "partial", itemKey: "userId.name" },
      customerId: { type: "partial", itemKey: "userId.clientId" },
      courierName: { type: "exact", itemKey: "courierId" },
      awbs: {
        type: "comma-api",
        itemKey: "awbNumber",
        model: "Shipment",
        endpoint: "/common/search",
      },
      action: { type: "exact", itemKey: "disputeStatus" },
      shippingId: { type: "comma", itemKey: "shipmentId" },
      date: { type: "date", itemKey: "disputeDate" },
    }),
    []
  );

  // Memoize filtered data to prevent unnecessary re-renders
  const { data: finalFilteredData, isLoading } = useFilteredData(
    weightDisputes,
    appliedFilters,
    filterConfig
  );

  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Customer Name",
        accessorKey: "userId.name",
        meta: {
          tooltip: (row) => row.userId.name || "N/A",
        },
      }),
      ColumnHelper({
        header: "Customer Id",
        accessorKey: "userId.clientId",
        meta: {
          tooltip: (row) => row.userId.clientId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Applied  Date",
        accessorKey: "disputeDate",
        cell: (info) => formatDate(info.row.original.disputeDate),
        meta: {
          tooltip: (row) => row.disputeDate || "N/A",
        },
      }),
      ColumnHelper({
        header: "Entered Weight",
        accessorKey: "actualWeight",
        cell: (info) => `${info.row.original.order.physicalWeight} kg`,
        meta: {
          tooltip: (row) => `${row.order.physicalWeight} kg`,
        },
      }),
      ColumnHelper({
        header: "Dimensions (in cm)",
        accessorKey: "dimension",
        cell: (info) => {
          const { length, breadth, height } =
            info.row.original.order.dimensions;
          return `${length} x ${breadth} x ${height}`;
        },
        meta: {
          tooltip: (row) => {
            const { length, breadth, height } = row.order.dimensions;
            return `${length} x ${breadth} x ${height}`;
          },
        },
      }),
      ColumnHelper({
        header: "Applied Weight",
        accessorKey: "appliedWeight",
        cell: (info) => `${info.row.original.appliedWeight} kg`,
        meta: {
          tooltip: (row) => `${row.appliedWeight} kg`,
        },
      }),
      ColumnHelper({
        header: "Shipment ID",
        accessorKey: "shipmentId",
        meta: {
          tooltip: (row) => row.shipmentId,
        },
      }),
      ColumnHelper({
        header: "Products",
        accessorKey: "products",
        cell: (info) => {
          const products = info.row.original.order.products
            .map((product) => product.productName)
            .join(", ");
          return <span>{products}</span>;
        },
        meta: {
          tooltip: (row) =>
            row.order.products.map((p) => p.productName).join(", "),
        },
      }),
      ColumnHelper({
        header: "Shipping Charge",
        accessorKey: "freightCharge",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.freightCharge)}
          </span>
        ),
        meta: {
          tooltip: (row) => (
            <span className="font-nunito">
              {formatCurrency(row.freightCharge)}
            </span>
          ),
        },
      }),
      ColumnHelper({
        header: "Courier",
        accessorKey: "courierId",
        cell: (info) =>
          courier.find((c) => c._id === info.row.original.courierId)?.name,
        meta: {
          tooltip: (row) => courier.find((c) => c._id === row.courierId)?.name,
        },
      }),
      ColumnHelper({
        header: "AWB",
        accessorKey: "awbNumber",
        meta: {
          tooltip: (row) => row.awbNumber,
        },
      }),
      ColumnHelper({
        header: "Action",
        accessorKey: "disputeStatus",
        cell: (info) => (
          <span className="font-bold font-nunito text-secondary/95">
            {info.row.original.disputeStatus}
          </span>
        ),
        meta: {
          tooltip: (row) => row.disputeStatus,
        },
      }),
    ],
    []
  );

  // Handle filter changes
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);
  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  // Apply filters without resetting raw data
  const handleApply = useCallback(() => {
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  // Reset filters properly
  const handleReset = useCallback(() => {
    const initialFilters = {
      customerName: "",
      customerId: "",
      courierName: "",
      ShippingId: "",
      awbs: "",
      action: "",
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);
  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        {/* Filters Section */}
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetContent aria-describedby="">
            <SheetHeader>
              <SheetTitle>Update Weight Disputes</SheetTitle>
            </SheetHeader>
            <UpdateWeightDispute
              setSheetOpen={setIsSheetOpen}
              handelPlaceOrder={() => {}} // Fixed empty function
            />
          </SheetContent>
        </Sheet>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <DateInput
            label="Date Filter"
            placeholder="Select From & To date"
            mode="range"
            value={filters.date}
            onChange={(value) => handleChange("date", value)}
          />
          <InputField
            label="Customer Name"
            placeholder="Enter Customer Name"
            name="customerName"
            value={filters.customerName}
            onChange={(value) => handleChange("customerName", value)}
          />
          <InputField
            label="Customer ID"
            placeholder="Enter Customer ID"
            name="customerId"
            value={filters.customerId}
            onChange={(value) => handleChange("customerId", value)}
          />
          <DropDownInput
            label="Courier Name"
            placeholder="Select Courier Name"
            options={courierOptions}
            value={filters.courierName}
            onChange={(value) => handleChange("courierName", value)}
          />
        </div>

        {/* AWB & Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <InputField
            label="Shipping ID"
            placeholder="Enter Shipping ID Comma Separated"
            name="shippingId"
            value={filters.shippingId}
            onChange={(value) => handleChange("shippingId", value)}
          />
          <InputField
            label="AWBs"
            placeholder="AWBs Comma Separated"
            name="awbs"
            value={filters.awbs}
            onChange={(value) => handleChange("awbs", value)}
          />
          <DropDownInput
            label="Action"
            placeholder="Select Action"
            options={[
              { label: "Accepted", value: "Accepted" },
              { label: "Rejected", value: "Rejected" },
              { label: "Auto Accepted", value: "Auto Accepted" },
              { label: "Pending", value: "Pending" },
              { label: "Raised Dispute", value: "Dispute Raised" },
              { label: "Resolved", value: "Resolved" },
            ]}
            value={filters.action}
            onChange={(value) => handleChange("action", value)}
          />
          <div className="flex w-full md:w-fit gap-2 justify-end md:justify-start">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleReset}
              className="h-12"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Option Group Section */}
        <DataTable
          columns={columns}
          tittle="Weight Dispute"
          data={finalFilteredData}
          isLoading={isLoading}
          resetPage={resetPageCounter}
          showCheckbox={false}
          actions={[
            {
              name: "Upload",
              onClick: () => {
                setIsSheetOpen(true);
              },
              icon: HiOutlineUpload,
            },
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Admin: Export Weight Dispute",
                filename: `Weight_Dispute_${new Date().getTime()}`,
              },
            },
          ]}
        />
      </div>
    </RightWrapper>
  );
};

export default WeightDispute;
